<template>
    <b-modal
      centered
      size="lg"
      button-size="sm"
      :ok-title="'Save'"
      title="Report Lost Order"
      @ok="handleOk"
      @show="resetModal"
      @hidden="close"
      @cancel="close"
      :visible="isVisible">
      <template v-slot:modal-ok>
        <b-spinner v-if="loading" :disabled="loading" small></b-spinner>
        <span v-else>Save</span>
      </template>
      <b-form ref="form" @submit.stop.prevent="handleSubmit">
        <input v-if="order" type="hidden" name="orderID" :value="order.order_id">
        <b-form-group
          label="Support Ticket *"
          label-size="lg">
          <b-row>
            <b-form-input
              v-model="form.support_ticket_url.value"
              placeholder="Insert support ticket url..."
              required
              :state="form.support_ticket_url.is_valid"
              @input="updateValidity('support_ticket_url')">
            </b-form-input>
            <b-form-invalid-feedback :state="form.support_ticket_url.is_valid">
              {{ form.support_ticket_url.feedback }}
            </b-form-invalid-feedback>
          </b-row>
        </b-form-group>
        <b-form-group
          label="Reason *"
          label-size="lg">
          <b-row>
            <b-form-select
              v-model="form.reason.value"
              :options="reasons"
              :class="{ 'placeholder-active': !form.reason.value }"
              :state="form.reason.is_valid"
              @change="updateValidity('reason')"
              required
              url>
              <option disabled value=null>Select a reason to this report...</option>
            </b-form-select>
            <b-form-invalid-feedback :state="form.reason.is_valid">
              {{ form.reason.feedback }}
            </b-form-invalid-feedback>
          </b-row>
        </b-form-group>
        <b-form-group
          v-if="form.reason.value == 'Other'"
          label="Other reason *"
          label-size="lg">
          <b-row>
            <b-form-input
              type="text"
              placeholder="Describe with more detail the other reason..."
              v-model="form.other_reason.value"
              invalid-feedback="Reason description required."
              :state="form.other_reason.is_valid"
              required
              @input="updateValidity('other_reason')">
            </b-form-input>
            <b-form-invalid-feedback :state="form.other_reason.is_valid">
              {{ form.other_reason.feedback }}
            </b-form-invalid-feedback>
          </b-row>
        </b-form-group>
      </b-form>
    </b-modal>
</template>

<script>
import { required, requiredIf, url } from 'vuelidate/lib/validators';
import errorHandler from '@/mixins/errorHandler';
import Vue from 'vue';
import { ORDER_STATUS } from '@/scripts/constants';


export default {
  name: 'modal-report-lost',
  mixins: [ errorHandler ],
  data() {
    return {
      form: {
        reason: {
          value: null,
          is_valid: null,
          feedback: '',
        },
        other_reason: {
          value: null,
          is_valid: null,
          feedback: '',
        },
        support_ticket_url: {
          value: null,
          is_valid: null,
          feedback: '',
        },
        status: ORDER_STATUS.Lost,
      },
      loading: false,
      reasons: [ 'Lost', 'Stolen', 'Other' ],
    };
  },
  props: {
    order: {
      type: Object,
      default: () => ({
        order_id: null,
      }),
    },
    isVisible: Boolean,
  },
  computed: {
    rules() {
      return {
        loading: false,
        form: {
          reason: {
            value: {
              required,
            },
          },
          other_reason: {
            value: {
              requiredIf: requiredIf(() => this.form.reason.value === 'Other'),
            },
          },
          support_ticket_url: {
            value: {
              required,
              url,
            },
          },
        },
      };
    },
  },
  methods: {
    checkFormValidity() {
      const fields = [
        'reason',
        'support_ticket_url',
        'other_reason',
      ];

      let allValid = true;
      fields.forEach(field => {
        const fieldValue = `form.${field}.value`;
        const isValid = this.isValid(fieldValue);
        this.form[field].is_valid = isValid;
        if (!isValid) {
          allValid = false;
          this.form[field].feedback = this.getFeedbackMessage(field);
        } else {
          this.form[field].feedback = '';
        }
      });

      return allValid;
    },
    // Map the field to readable feedback messages
    getFeedbackMessage(field) {
      switch (field) {
        case 'reason':
          return 'Reason is required.';
        case 'support_ticket_url':
          return 'Must be a valid URL (Ex: https://www.example.com)';
        case 'other_reason':
          return 'Other reason description is required.';
        default:
          return 'This field is required.';
      }
    },
    updateValidity(field) {
      const isValid = this.isValid(`form.${field}`);
      this.$set(this.form[field], 'is_valid', isValid);
      if (!isValid) {
        this.form[field].feedback = this.getFeedbackMessage(field);
      } else {
        this.form[field].feedback = '';
      }
    },
    ok() {
      this.$emit('ok');
      this.isVisible = false;
    },
    close() {
      this.$emit('close');
    },
    resetModal() {
      // clear the form data
      this.form.reason.value = null;
      this.form.other_reason.value = null;
      this.form.support_ticket_url.value = null;
      this.form.reason.is_valid = null;
      this.form.other_reason.is_valid = null;
      this.form.support_ticket_url.is_valid = null;
    },
    async handleSubmit() {
      if (!this.checkFormValidity()) {
        const errMsg = 'The form is not valid. Please check the fields.';
        Vue.prototype.$noty.error(errMsg);
        return;
      }

      const proceed = await this.$bvModal.msgBoxConfirm(
        'Kit will be disabled and orders updated to their final statuses. This action is non reversible. Are you sure you want to continue?',
        {
          title: 'Alert',
          okTitle: 'Continue',
          headerBgVariant: 'danger',
          headerTextVariant: 'light',
          okVariant: 'danger',
          footerClass: 'p-2 border-bottom-0',
          centered: true,
          html: true,
        },
      );
      if (!proceed) {
        return;
      }

      if (this.form.reason.value === 'Other') {
        this.form.reason.value = this.form.other_reason.value;
      }

      const payload = {
        orderID: this.order.order_id,
        status: ORDER_STATUS.Lost,
        support_ticket_url: this.form.support_ticket_url.value,
        reason: this.form.reason.value,
      };

      this.loading = true;
      this.$store.dispatch('OrderManagement/updateOrderStatus', payload)
        .then(() => {
          this.$router.go();
        })
        .catch(error => {
          console.error('Unable to update order status:', error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handleOk(bvModalEvent) {
      bvModalEvent.preventDefault();
      this.handleSubmit();
    },
  },
  validations() {
    return this.rules;
  },
};
</script>

<style scoped>

.placeholder-active {
  color: #6c757d;
}
</style>
